import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Vitalität',
    description: 'Anonymisierte, indikative Wohlbefindens-Trends auf Gruppenebene.',
    noScope: 'Wähle ein Unternehmen, um die Vitalitätszahlen zu sehen.',
    noDepartments: 'Keine Abteilung hier hat Statistiken, die du sehen darfst.',
    disclaimer:
      'Alle Werte sind <strong>indikative Wohlbefindens-Einblicke</strong> und <strong>keine medizinische Diagnose</strong>. Trends werden nur bei ausreichend vielen Teilnehmenden angezeigt (Privacy by Design).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Abteilung',
    },
    location: {
      label: 'Standort',
      all: 'Alle Standorte',
    },
  },
  badge: {
    measured: 'Gemessen',
    aiDerived: 'KI-Einblick',
  },
  level: {
    good: 'Gut',
    opportunity: 'Chance',
  },
  statusPill: {
    healthy: 'Gesundes Niveau',
    opportunity: 'Chance zu helfen',
    strongForward: 'Stark & steigend',
  },
  trend: {
    flat: 'Keine Veränderung',
    vsPrevious: 'ggü. vorheriger Periode',
    vsAverage: 'ggü. Durchschnitt',
    up: 'Steigend',
    down: 'Leicht niedriger',
    upBad: 'Leicht höher',
    week: 'Wo.',
  },
  units: {
    hoursMinutes: '{{h}}h {{m}}m',
    average: 'Ø {{value}}',
  },
  status: {
    noData: 'Keine Daten in diesem Zeitraum.',
  },
  table: {
    level: 'Niveau',
    trend: 'Trend',
    action: 'Maßnahme',
    actionNone: '—',
    actionLink: 'Abteilung',
    actionLinkTitle: 'Zu dieser Abteilung gehen',
  },
  insight: {
    title: 'Was die Vitalitätszahlen zeigen',
    unavailable: 'Noch nicht genug Daten für eine Zusammenfassung.',
    vitality: {
      up: 'Die Vitalität steigt ({{pct}}% {{period}}) bei einer Teilnahmequote von {{participation}}%.',
      down: 'Die Vitalität sinkt ({{pct}}% {{period}}) bei einer Teilnahmequote von {{participation}}%.',
      flat: 'Die Vitalität ist stabil ({{pct}}% {{period}}) bei einer Teilnahmequote von {{participation}}%.',
      noParticipation: 'Die Vitalität ist stabil ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'in den letzten 7 Tagen',
      last30Days: 'diesen Monat',
      thisWeek: 'diese Woche',
      thisMonth: 'diesen Monat',
      custom: 'in diesem Zeitraum',
    },
    stress: {
      up: 'Der Stress steigt ({{pct}}%)',
      down: 'der Stress lässt nach ({{pct}}%)',
      flat: 'der Stress bleibt stabil',
    },
    sleep: {
      longer: 'und die Mitarbeitenden schlafen im Schnitt {{minutes}} Min. länger.',
      shorter: 'und die Mitarbeitenden schlafen im Schnitt {{minutes}} Min. weniger.',
      same: 'und die Mitarbeitenden schlafen etwa gleich viel.',
    },
    mostActiveDay: '{{day}} ist der aktivste Tag.',
  },
  cards: {
    vitalityScore: {
      title: 'Vitalitätswert',
      unit: '%',
      info: 'Ein zusammengesetzter Wert aus Schlaf, Aktivität, HRV, Stress und Ruheherzfrequenz.',
    },
    sleep: {
      title: 'Schlaf',
      unit: '',
      info: 'Durchschnittliche Schlafdauer pro Nacht.',
    },
    stress: {
      title: 'Stress',
      unit: '%',
      info: 'Gemessenes Stressniveau; je niedriger, desto ruhiger.',
    },
    recovery: {
      title: 'Erholung',
      unit: '%',
      info: 'Wie gut sich der Körper erholt, basierend auf Schlaf, HRV und Stress.',
    },
    activity: {
      title: 'Aktivität',
      unit: 'Schritte/Tag',
      info: 'Durchschnittliche Anzahl Schritte pro Tag.',
    },
    hrv: {
      title: 'HRV',
      unit: 'ms',
      info: 'Herzfrequenzvariabilität — ein Indikator für Erholung und Widerstandsfähigkeit.',
    },
    restingHeartRate: {
      title: 'Ruheherzfrequenz',
      unit: 'bpm',
      info: 'Die niedrigste gemessene Herzfrequenz im gewählten Zeitraum, als Näherung der Ruheherzfrequenz.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'Die Vitalität entwickelt sich gut — die aktuelle Routine beibehalten.',
      opportunity:
        'Bei der Vitalität ist noch Luft nach oben — ein kurzes Team-Check-in kann zeigen, woran es hakt.',
    },
    sleep: {
      good: 'Der Schlaf sieht im Team gesund aus — keine Maßnahme nötig.',
      opportunity:
        'Der Schlaf liegt unter dem Zielwert — ein Hinweis auf eine feste Abendroutine und weniger Bildschirmzeit vor dem Schlafen kann helfen.',
    },
    stress: {
      good: 'Das Stressniveau ist unter Kontrolle — weiter beobachten.',
      opportunity:
        'Der Stress ist erhöht — ein kurzes Gespräch über die Arbeitsbelastung oder eine erholungsfördernde Aktivität kann helfen.',
    },
    recovery: {
      good: 'Die Erholung ist solide — das Team erholt sich gut nach Belastung.',
      opportunity:
        'Die Erholung hinkt hinterher — prüfen, ob Ruhetage und Arbeitsbelastung im Gleichgewicht sind.',
    },
    activity: {
      good: 'Das Aktivitätsniveau ist gesund — keine Maßnahme nötig.',
      opportunity:
        'Die Aktivität liegt unter dem Zielwert — ein Walking-Meeting oder eine Schritte-Challenge kann helfen.',
    },
    hrv: {
      good: 'Die HRV sieht gesund aus — keine Maßnahme nötig.',
      opportunity:
        'Die HRV ist niedriger als erwartet — das hängt oft mit Stress oder schlechtem Schlaf zusammen; gemeinsam besprechen.',
    },
    restingHeartRate: {
      good: 'Die Ruheherzfrequenz liegt in einem gesunden Bereich.',
      opportunity:
        'Die Ruheherzfrequenz ist erhöht — zusammen mit Stress oder weniger Schlaf ist das ein Hinweis wert.',
    },
  },
};
